import React, { useState, useMemo } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import PolicyModal from '@/components/PolicyModal';
import Nav from '@/components/Nav';
import CategoryChips from '@/components/CategoryChips';
import ProductCard from '@/components/ProductCard';
import ContactForm from '@/components/ContactForm';
import { PRODUCTS, CATEGORIES } from '@/data/products';

const FEATURED_IDS = [2, 4, 5]; // Orange, Pineapple, Apple — in stock, have images

function FeaturedCard({ product }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <a
      href="#products"
      className="group bg-parchment-mid border border-border-warm rounded-[2px] p-5 flex flex-col items-center gap-4 hover:border-ink-deep transition-colors no-underline"
    >
      <div className="w-full aspect-square flex items-center justify-center p-5 rounded-[2px] overflow-hidden" style={{ backgroundColor: '#ede8dfd4' }}>
        <img
          src={product.image}
          alt={`${product.brand} ${product.name}`}
          className={`w-full h-full object-contain transition-[transform,opacity] duration-300 group-hover:scale-[1.04] ${loaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ mixBlendMode: 'multiply', filter: 'brightness(1.04) saturate(0.85)' }}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
      </div>
      <div className="text-center">
        <p className="font-sans text-[0.625rem] font-medium tracking-[0.12em] uppercase text-ink-muted mb-1">{product.brand}</p>
        <p className="font-serif text-lg font-normal text-ink-deep">{product.name}</p>
      </div>
    </a>
  );
}

const CONTACT_INFO = [
  { icon: Phone, label: 'Phone', value: '+1 (614) 607-8598', href: 'tel:+16146078598' },
  { icon: Mail,  label: 'Email', value: 'contact@ctoimports.co', href: 'mailto:contact@ctoimports.co' },
  { icon: MapPin, label: 'Location', value: 'Columbus, Ohio, US', href: null },
];

function buildCounts(products) {
  const counts = { total: products.length };
  CATEGORIES.forEach(cat => {
    if (cat !== 'All') counts[cat] = products.filter(p => p.category === cat).length;
  });
  return counts;
}

export default function CatalogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openPolicy, setOpenPolicy] = useState(null); // 'privacy' | 'terms' | null

  const filtered = useMemo(
    () => activeCategory === 'All' ? PRODUCTS : PRODUCTS.filter(p => p.category === activeCategory),
    [activeCategory]
  );

  const counts = useMemo(() => buildCounts(PRODUCTS), []);
  const featuredProducts = useMemo(() => PRODUCTS.filter(p => FEATURED_IDS.includes(p.id)), []);

  return (
    <div className="min-h-screen bg-parchment">
      <Nav />

      {/* ── Home ─────────────────────────────────────────────────────────── */}
      <section id="home" className="bg-ink-deep px-6 pt-36 pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-sans text-[0.6875rem] font-medium tracking-[0.14em] uppercase text-gold-edge mb-5">
            Direct from Algeria
          </p>
          <div className="w-10 h-px bg-gold-edge mx-auto mb-10" />
          <h1 className="font-serif font-light text-[clamp(3rem,7vw,5.5rem)] leading-[0.92] tracking-[-0.02em] text-parchment mb-8">
            Premium Algerian<br className="hidden sm:block" /> Imports.
          </h1>
          <p className="font-sans text-base text-[#a09486] leading-[1.65] max-w-[54ch] mx-auto mb-12">
            CTO Imports and Distribution sources premium Algerian products directly from their producers —
            bringing quality goods across categories to wholesale buyers, distributors, and food-service
            operators across the United States.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap mb-16">
            <a
              href="#products"
              className="inline-flex items-center bg-parchment text-ink-deep px-8 py-3 rounded-[2px] font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase transition-colors duration-[180ms] hover:bg-gold-edge hover:text-parchment"
            >
              View Products
            </a>
            <a
              href="#contact"
              className="inline-flex items-center text-parchment px-8 py-3 rounded-[2px] font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase border border-parchment/30 transition-colors duration-[180ms] hover:border-parchment/70"
            >
              Request Catalogue
            </a>
          </div>
          <div className="flex items-center justify-center gap-6 pt-8 border-t border-parchment/10">
            <span className="font-serif text-sm font-light tracking-[0.1em] text-[#a09486]">IFRI · Beverages</span>
            <div className="w-px h-4 bg-parchment/20" />
            <span className="font-serif text-sm font-light tracking-[0.1em] text-[#a09486]">El Mordjene · Patisserie</span>
          </div>
        </div>
      </section>

      {/* ── Featured products ─────────────────────────────────────────────── */}
      {/* <section className="bg-parchment py-16 px-6 border-b border-border-warm">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <p className="font-sans text-[0.6875rem] font-medium tracking-[0.12em] uppercase text-ink-muted mb-2">Featured</p>
              <h2 className="font-serif text-2xl font-normal text-ink-deep">Available Now</h2>
            </div>
            <a
              href="#products"
              className="font-sans text-[0.6875rem] tracking-[0.08em] uppercase text-ink-muted hover:text-ink-deep transition-colors"
            >
              View all →
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {featuredProducts.map(product => (
              <FeaturedCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section> */}

      {/* ── Products ─────────────────────────────────────────────────────── */}
      <section id="products" className="bg-parchment-mid py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-baseline justify-between mb-8 pb-6 border-b border-border-warm">
            <h2 className="font-serif text-[clamp(1.75rem,3vw,2.25rem)] font-normal leading-[1.1] text-ink-deep">
              Products
            </h2>
            <span className="font-sans text-[0.6875rem] text-ink-muted tracking-[0.08em] uppercase">
              {filtered.length} {filtered.length === 1 ? 'item' : 'items'}
            </span>
          </div>

          <CategoryChips
            active={activeCategory}
            onSelect={setActiveCategory}
            counts={counts}
          />

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {filtered.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="py-24 text-center">
              <p className="font-serif text-2xl font-light text-ink-muted">No products in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── About ────────────────────────────────────────────────────────── */}
      <section id="about" className="bg-parchment py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-[0.6875rem] font-medium tracking-[0.12em] uppercase text-ink-muted mb-4">
            About CTO
          </p>
          <div className="w-8 h-px bg-gold-edge mb-8" />
          <h2 className="font-serif text-[clamp(1.75rem,3vw,2.25rem)] font-normal leading-[1.1] text-ink-deep mb-6">
            Direct from Algeria.
          </h2>
          <div className="space-y-4 max-w-[68ch]">
            <p className="font-sans text-base text-ink-body leading-[1.65]">
              CTO Imports and Distribution sources quality Algerian products directly from their producers,
              without intermediaries. We work with established Algerian brands across multiple categories
              and bring them to wholesale buyers, distributors, and retail chains across the United States.
            </p>
            <p className="font-sans text-base text-ink-body leading-[1.65]">
              Our catalogue currently spans beverages, patisserie ingredients, spreads, baking essentials,
              and more — with new product lines and brand partnerships being added on an ongoing basis.
            </p>
            <p className="font-sans text-base text-ink-body leading-[1.65]">
              If you are looking for a specific Algerian product or interested in a volume arrangement,
              reach out directly — we are always open to sourcing new items for the right partners.
            </p>
          </div>
        </div>
      </section>

      {/* ── Contact ──────────────────────────────────────────────────────── */}
      <section id="contact" className="bg-parchment-mid py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="font-sans text-[0.6875rem] font-medium tracking-[0.12em] uppercase text-ink-muted mb-4">
            Get in Touch
          </p>
          <div className="w-8 h-px bg-gold-edge mb-8" />
          <p className="font-sans text-base text-ink-muted leading-[1.65] mb-8 max-w-[52ch]">
            Tell us what you're looking for. We'll follow up with pricing, minimum order quantities, and availability.
          </p>

          {/* Contact info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            {CONTACT_INFO.map(({ icon: Icon, label, value, href }) => {
              const inner = (
                <>
                  <div className="flex items-center justify-center w-12 h-12 rounded-[4px] bg-ink-deep text-parchment mb-4 mx-auto">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <p className="font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase text-ink-deep mb-1.5">
                    {label}
                  </p>
                  <p className="font-sans text-sm font-normal text-ink-muted leading-snug">
                    {value}
                  </p>
                </>
              );
              return href ? (
                <a
                  key={label}
                  href={href}
                  className="flex flex-col items-center text-center bg-parchment border border-border-warm rounded-[4px] px-6 py-8 transition-colors duration-150 hover:border-ink-deep no-underline"
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={label}
                  className="flex flex-col items-center text-center bg-parchment border border-border-warm rounded-[4px] px-6 py-8"
                >
                  {inner}
                </div>
              );
            })}
          </div>

          <ContactForm />
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="bg-ink-deep py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col leading-none">
            <span className="font-serif text-xl font-light tracking-[0.06em] text-parchment">CTO</span>
            <span className="font-sans text-[0.5rem] font-medium tracking-[0.14em] uppercase text-[#a09486] mt-0.5">Import and Distribution</span>
          </div>
          <p className="font-sans text-[0.6875rem] text-[#a09486] tracking-[0.05em] text-center">
            © {new Date().getFullYear()} CTO Imports and Distribution · Columbus, Ohio, USA
          </p>
          <div className="flex items-center gap-5">
            <button
              onClick={() => setOpenPolicy('privacy')}
              className="font-sans text-[0.6875rem] text-[#a09486] tracking-[0.05em] hover:text-parchment transition-colors underline underline-offset-2"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setOpenPolicy('terms')}
              className="font-sans text-[0.6875rem] text-[#a09486] tracking-[0.05em] hover:text-parchment transition-colors underline underline-offset-2"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {openPolicy && (
        <PolicyModal type={openPolicy} onClose={() => setOpenPolicy(null)} />
      )}
    </div>
  );
}
